import React, { useEffect, useState } from "react";
import {
  COLORS,
  DropdownPopover,
  TitleSmall,
} from "@bikdotai/bik-component-library";
import { populateChannelIdsDropdownForFlow } from "../../services/helpers/domainScoping";
import { useFlowScope } from "../../hooks/useFlowScope";
import { useAppSelector } from "../../state/store";
import { InstaDropdownContainer, InstaDropdownWrapper } from "./styled";
import WhatsappIcon from "../../icons/whatsappIcon";
import InstagramIcon from "../../icons/instagramIcon";
import EmailIcon from "../../icons/email";
import ChevronDownIcon from "../../icons/chevron_down";

export type ChannelAccountOption = {
  label: string;
  value: string;
  selected: boolean;
};

export interface FlowChannelAccounts {
  options: ChannelAccountOption[];
  selected: Partial<ChannelAccountOption>;
  isLoading: boolean;
  select: (option: ChannelAccountOption) => void;
}

/**
 * The WABA number / IG account / mailbox a trigger belongs to, scoped to the
 * flow's domain. Shared by the trigger modals that show the account picker in
 * their header.
 */
export const useFlowChannelAccounts = (
  channel: string,
  currentChannelId?: string
): FlowChannelAccounts => {
  const [options, setOptions] = useState<ChannelAccountOption[]>([]);
  const [selected, setSelected] = useState<Partial<ChannelAccountOption>>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const channels: { [key: string]: any } = useAppSelector(
    (state) => state.flowMetaState.channels!
  );
  const flowMeta: { [key: string]: any } = useAppSelector(
    (state) => state.flowMetaState.flowMeta
  );
  const flowId = useAppSelector((state) => state.homeState.flowId);
  const { domain, domainChannelMap, channelSettingsId } = useFlowScope();

  useEffect(() => {
    populateChannelIdsDropdownForFlow({
      channel,
      channels,
      setIsLoading,
      setSelectedChannel: setSelected,
      setChannelIdOptions: setOptions,
      currentChannelId: currentChannelId || channelSettingsId,
      domain,
      domainChannelMap,
      flowMetaEntry: flowMeta[flowId],
    });
  }, [domainChannelMap, currentChannelId, channelSettingsId]);

  useEffect(() => {
    if (options && options.length === 1) {
      setSelected(options[0]);
    } else if (
      selected?.value &&
      selected.value !== "ERROR" &&
      options &&
      !options.some((option) => option.value === selected.value)
    ) {
      // Clear a selection no longer valid for this flow's domain.
      setSelected({});
    }
  }, [options]);

  const select = (option: ChannelAccountOption) => {
    setSelected(option);
    setOptions(
      options.map((item) => ({ ...item, selected: item.value === option.value }))
    );
  };

  return { options, selected, isLoading, select };
};

const getChannelIcon = (channel: string) => {
  if (channel === "whatsapp") {
    return <WhatsappIcon />;
  } else if (channel === "instagram") {
    return <InstagramIcon />;
  }
  return <EmailIcon />;
};

const ChannelAccountDropdown: React.FC<
  FlowChannelAccounts & { channel: string }
> = ({ channel, options, selected, isLoading, select }) => {
  return (
    <InstaDropdownContainer>
      <DropdownPopover
        width="200px"
        disabled={isLoading}
        onSelect={(option: any) => select(option)}
        options={options}
      >
        <InstaDropdownWrapper>
          {getChannelIcon(channel)}
          <TitleSmall className="dropdown-text" style={{ fontWeight: 400 }}>
            {selected.label || "Select an account"}
          </TitleSmall>
          <ChevronDownIcon color={COLORS.content.primary} />
        </InstaDropdownWrapper>
      </DropdownPopover>
    </InstaDropdownContainer>
  );
};

export default ChannelAccountDropdown;
